import React, { Fragment, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import toast from 'react-hot-toast';
import arrowLeftGreyIcon from 'assets/images/icons/arrow-left-grey.svg';
import chevronGreyIcon from 'assets/images/icons/chevron-grey.svg';
import plusIcon from 'assets/images/icons/plus.svg';
import settingGreyIcon from 'assets/images/icons/setting-grey.svg';
import AddDashboardOrPage from './AddDashboardOrPage';
import DashboardAddWidgetModal from './DashboardAddWidgetModal/DashboardAddWidgetModal';
import FormButton from 'components/form-components/FormButton';
import NestedSelect from 'components/form-components/NestedSelect';
import PageWidgetsEditView from './PageWidgetsEditView';
import PageWidgetsView from './PageWidgetsView';
import { CallAPI } from 'actions/General';
import FormLoadingButton from 'components/form-components/FormLoadingButton';
import { DEFAULT_NUMBER_OF_ROWS, getMaxTakenRows } from './utils';
import EmptyDashboardPage from './charts/EmptyDashboardPage';

const DashboardView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dashboardId = location.state.dashboardId ?? null;

  const [, setFetchLoading] = useState(false);
  const [dashboardDetails, setDashboardDetails] = useState(null);
  const [pageOptionList, setPageOptionList] = useState([]);
  const [selectedPageId, setSelectedPageId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showAddWidgetModal, setShowAddWidgetModal] = useState(false);
  const [showAddEditPageModal, setShowAddEditPageModal] = useState(null);
  const [currentWidgetsRows, setCurrentWidgetsRows] = useState(DEFAULT_NUMBER_OF_ROWS);
  const [widgetList, setWidgetList] = useState([]);
  const [originalWidgetList, setOriginalWidgetList] = useState([]);
  const [saveLoader, setSaveLoader] = useState(false);

  const getDashboardDetails = async () => {
    const result = await CallAPI('GET_DASHBOARD_DETAIL', null, dashboardId, setFetchLoading);
    if (result.status) {
      setDashboardDetails(result.data);
      if (result.data.pages.length) {
        if (location.state.pageId) setSelectedPageId(location.state.pageId);
        else setSelectedPageId(result.data.pages[0]._id);
        const pagesOptions = result.data.pages.map((p) => ({
          value: p._id,
          displayValue: p.pageTitle,
        }));
        setPageOptionList(pagesOptions);
      }
    }
  };

  const getDisplayValue = (list, checkValue, defaultValue = '') => {
    return list.find((o) => o.value === checkValue)?.displayValue ?? defaultValue;
  };

  const getModifiedPositions = () => {
    const originalLookup = {};
    const modified = [];

    originalWidgetList.forEach((w) => {
      originalLookup[w._id] = w;
    });

    widgetList.forEach((w) => {
      const o = originalLookup[w._id];
      if (!o) return;

      if (
        w.startRow !== o.startRow ||
        w.startColumn !== o.startColumn ||
        w.takeRows !== o.takeRows ||
        w.takeColumns !== o.takeColumns
      ) {
        modified.push({
          widgetId: w._id,
          startRow: w.startRow,
          startColumn: w.startColumn,
          takeRows: w.takeRows,
          takeColumns: w.takeColumns,
        });
      }
    });

    return modified;
  };

  const handleSave = async () => {
    const modifiedWidgetsPosition = getModifiedPositions();

    if (modifiedWidgetsPosition.length) {
      const result = await CallAPI('UPDATE_DASHBOARD_WIDGET_POSITION', {
        widgets: modifiedWidgetsPosition,
      });
      if (result.status) {
        toast.success('alert_success_dashboard_widget_position_updated');
        setEditMode(false);
      } else {
        toast.error(result.error);
      }
    } else {
      setEditMode(false);
    }
  };

  const getPageDetails = async (withWidgets = true) => {
    const queryParams = withWidgets ? '?withWidgets=true' : '';
    const result = await CallAPI(
      'GET_DASHBOARD_PAGE_DETAIL',
      null,
      `${selectedPageId}${queryParams}`,
    );
    if (result.status) {
      // setPageDetails(page);
      const maxTakenRows = getMaxTakenRows(result.data.widgets);
      setCurrentWidgetsRows(maxTakenRows);

      const widgets = cloneDeep(result.data.widgets);
      setOriginalWidgetList(widgets);
      setWidgetList(cloneDeep(widgets));
    }
  };

  useEffect(() => {
    if (dashboardId) getDashboardDetails();
  }, []);

  return (
    <Fragment>
      {dashboardDetails && (
        <div className='dashboard-view'>
          <div className='d-flex gap-2 align-items-center'>
            <img
              className='cursor-pointer mt-1'
              src={arrowLeftGreyIcon}
              onClick={() => navigate(-1)}
            />
            <label className='theme-font-inter-semi-bold theme-size-1_6 theme-text-black-1'>
              {dashboardDetails.dashboardTitle}
            </label>
          </div>
          <div className='theme-background-white-1 p-3 d-flex justify-content-between'>
            <div className='d-flex gap-3 align-items-center'>
              <NestedSelect
                options={pageOptionList}
                handleChange={(_, value) => setSelectedPageId(value)}
                name='selectedPageId'
                toggleClasses='theme-background-white-5 p-2'
                toggleComponent={
                  <div className='d-flex gap-2 align-items-center'>
                    <label className='cursor-pointer theme-text-white-'>
                      {getDisplayValue(pageOptionList, selectedPageId)}
                    </label>
                    <img src={chevronGreyIcon} className='rotate-180' />
                  </div>
                }
              />
              <div className='theme-width-40'>
                <div
                  className={`w-75 ratio ratio-1x1 rounded-circle cursor-pointer ${
                    editMode ? 'theme-background-grey-1' : 'theme-background-green-1'
                  }`}
                  onClick={() => {
                    if (!editMode) setShowAddEditPageModal('add');
                  }}
                >
                  <img src={plusIcon} className='svg-white cursor-pointer' />
                </div>
              </div>
            </div>
            <div className='d-flex gap-3'>
              {editMode ? (
                <>
                  <FormLoadingButton
                    text='button_save'
                    variant='white-2'
                    onClick={handleSave}
                    minWidth
                    loading={saveLoader}
                  />
                  <FormButton
                    text='button_add_widget'
                    icon={plusIcon}
                    iconClasses='svg-white'
                    variant='green-1'
                    onClick={() => setShowAddWidgetModal(true)}
                    disabled={saveLoader}
                  />
                </>
              ) : (
                <>
                  <FormButton
                    text='button_setting'
                    variant='white-1'
                    onClick={() => setShowAddEditPageModal('edit')}
                    icon={settingGreyIcon}
                    minWidth
                    disabled={saveLoader}
                  />
                  <FormButton
                    text='button_edit'
                    variant='green-1'
                    onClick={() => setEditMode(true)}
                    minWidth
                  />
                </>
              )}
            </div>
          </div>
          {editMode && widgetList.length === 0 && (
            <EmptyDashboardPage setShowAddWidgetModal={setShowAddWidgetModal} />
          )}
          {editMode ? (
            <PageWidgetsEditView
              selectedPageId={selectedPageId}
              getPageDetails={getPageDetails}
              currentWidgetsRows={currentWidgetsRows}
              widgetList={widgetList}
              setWidgetList={setWidgetList}
              dashboardId={dashboardId}
              setSaveLoader={setSaveLoader}
            />
          ) : (
            <PageWidgetsView selectedPageId={selectedPageId} companyId={dashboardDetails.company} />
          )}
        </div>
      )}

      {showAddWidgetModal && (
        <DashboardAddWidgetModal
          editMode={false}
          handleClose={() => setShowAddWidgetModal(false)}
          pageId={selectedPageId}
          dashboardId={dashboardId}
          widgetList={widgetList}
          currentWidgetsRows={currentWidgetsRows}
          getPageDetails={getPageDetails}
          setSaveLoader={setSaveLoader}
        />
      )}
      {showAddEditPageModal && (
        <AddDashboardOrPage
          editId={showAddEditPageModal === 'edit' ? selectedPageId : null}
          selectedDashboardId={dashboardId}
          onCancel={() => setShowAddEditPageModal(null)}
          refreshList={getDashboardDetails}
          type={'page'}
        />
      )}
    </Fragment>
  );
};

export default DashboardView;
